import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { COLORS, STATUS_COLORS } from "../theme/colors";
import { MATERIALS } from "../theme/materials";
import { getResponsiveCSS } from "../theme/globalStyles";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useAgentContext } from "../contexts/AgentContext";
import { SUPERVISOR_ROLES, getInitials } from "../utils/helpers";
import Logo from "./Logo";
import { Badge } from "./ui";
import Nav from "./Nav";
import AmbientOrbs from "./AmbientOrbs";
import Icon from "./Icon";
import { LLMNotConfigured } from "./LLMNotConfigured";
import { useProvidersConfigured } from "../hooks/useAvailableModels";
import { useConfigurationErrorToast } from "../hooks/useConfigurationErrorToast";
import SupervisorView from "../features/supervisor/SupervisorView";
import AdminView from "../features/admin/AdminView";
import ChunkTuningDocs from "../features/ingestion/ChunkTuningDocs";
import BuilderView from "../features/builder/BuilderView";
import AgentLibraryView from "../features/library/AgentLibraryView";
import AnalyticsView from "../features/analytics/AnalyticsView";
// Phase 1 sub-task E — full-page Conversation Inspector
import ConversationInspectorPage from "../features/analytics/ConversationInspectorPage";
import ObservabilityHub from "../features/analytics/observability/ObservabilityHub";
import ChannelsView from "../features/channels/ChannelsView";
import CampaignsView from "../features/campaigns/CampaignsView";
import SchedulesView from "../features/schedules/SchedulesView";
import MarketplaceView from "../features/marketplace/MarketplaceView";
// Skills v1 M7 (2026-07-01) — skill discovery / instance viewer / CL review
import SkillsView from "../features/skills/SkillsView";

const fmtVer = v => v ? `v${v.replace(/^v+/, "")}` : "";

export default function Shell() {
  const [loaded, setLoaded] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingEditAgent, setPendingEditAgent] = useState(null);
  const bp = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, currentTenant, onLogout, refreshTenant } = useAuth();
  const { agents, agentStatus, currentAgent } = useAgentContext();
  const { mode, toggleTheme } = useTheme();

  useEffect(() => { setLoaded(true); }, []);

  // BYOK-strict onboarding gate: surface a banner when the tenant has no LLM
  // configured. Don't render while Admin is open (the settings UI is right there).
  const { llmReady, loading: llmReadyLoading } = useProvidersConfigured();

  // Integration 412 banner — surfaces IntegrationConfigRequired errors with a
  // deep-link to the specific Admin card/field. Hook returns JSX or null.
  const configErrorBanner = useConfigurationErrorToast();

  // Check for pending edit agent from navigation state (library -> builder)
  useEffect(() => {
    if (location.state?.editAgent) {
      setPendingEditAgent(location.state.editAgent);
      // Clear the navigation state
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state]);

  // Agents is now the home page (was "builder"). Empty path defaults
  // to /library so a fresh login lands users on their agent list.
  // Split-on-/ + first-non-empty handles path parameters correctly:
  // /builder/<uuid> → activeTab="builder" + deepLinkAgentId="<uuid>".
  // (Pre-2026-05-24 fix: replace("/", "") produced "builder/<uuid>"
  // which failed the strict `activeTab === "builder"` checks below,
  // breaking deep-link URLs by rendering a blank view slot.)
  //
  // useParams() doesn't work for these children because App's outer
  // route is `path="/*"` (splat, no named params). The :agentId is
  // extracted here and passed as a prop instead.
  const _pathParts = location.pathname.split("/").filter(Boolean);
  const activeTab = _pathParts[0] || "library";
  const deepLinkAgentId = _pathParts[1] || null;

  const tabLabels = {
    builder: "Agent Builder", library: "Agents", marketplace: "Marketplace",
    channels: "Channels", campaigns: "Campaigns", schedules: "Schedules",
    supervisor: "Supervisor", analytics: "Analytics", admin: "Admin",
    skills: "Skills",
  };

  // Agents (library) listed first to match the sidebar order; Builder
  // follows. The list itself is still used for prev/next swipe nav and
  // breadcrumb labels — order matters for those flows.
  const tabs = ["library", "builder", "marketplace", "channels", "campaigns", "schedules",
    ...(SUPERVISOR_ROLES.includes(currentUser?.role) ? ["supervisor"] : []),
    "analytics", "admin"];

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Manrope', -apple-system, sans-serif",
      background: MATERIALS.gradientBackdrop(),
      color: COLORS.text,
      width: "100%", height: "100vh", display: "flex", flexDirection: "column",
      overflow: "hidden", opacity: loaded ? 1 : 0, transition: "opacity 0.5s",
      position: "relative",
    }}>
      <style>{getResponsiveCSS()}</style>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Shell-level ambient layer — every authenticated tab inherits the
          same backplane (gradient backdrop + ambient orbs). Work surfaces
          (Builder, Detail Viewer, Admin) keep solid card surfaces but now
          sit on the connected backdrop instead of pure-color bg. */}
      <AmbientOrbs />

      {/* Top bar — z-index 10 so the avatar's dropdown menu (which
          extends below the top bar) paints above the main content (z:1).
          Without this, the dropdown is rendered but hidden behind the
          main content area. */}
      <div style={{
        height: 48, borderBottom: `1px solid ${COLORS.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: bp.isMobile ? "0 12px" : "0 20px 0 0", flexShrink: 0,
        position: "relative", zIndex: 10,
        background: COLORS.surface,
      }}>
        {/* M3.4 — top bar slots: brand → co-brand → breadcrumb → right cluster.
            Tabs moved to the expanded sidebar (M3.1+M3.2). Co-brand and
            breadcrumb are reserved <div> placeholders so future tenant-
            logo + page-context features can target them without
            restructuring the top bar (decision #27). */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, height: "100%", minWidth: 0, overflow: "hidden", flex: 1 }}>
          {/* Brand slot — the real I² logo (mark + MIRRORSME wordmark +
              tagline), not a synthesized tile. Click → /library so the
              brand doubles as a "back to home" affordance. Ink follows
              the active theme (dark ink on the light surface, white ink
              on the dark surface) since the source asset is single-color. */}
          <div className="i2-topbar-brand" style={{
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <button
              onClick={() => navigate("/library")}
              title="I² Platform"
              style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Logo
                size={bp.isMobile ? 30 : 36}
                tone={mode === "dark" ? "white" : "dark"}
                hasCustomLogo={!!currentTenant?.has_custom_logo}
                aria-label="I² Platform — go to Agents"
              />
            </button>
          </div>
          {/* Divider between brand and co-brand */}
          {!bp.isMobile && <div style={{ width: 1, height: 20, background: COLORS.border, flexShrink: 0 }} />}
          {/* Co-brand slot — empty placeholder per decision #27 */}
          <div className="i2-topbar-cobrand" style={{
            minWidth: bp.isMobile ? 0 : 80,
            display: "flex", alignItems: "center",
          }}>
            {/* Future: tenant / partner logo renders here */}
          </div>
          {/* Breadcrumb slot — empty placeholder per decision #27 */}
          <div className="i2-topbar-breadcrumb" style={{
            flex: 1, minWidth: 0,
            display: "flex", alignItems: "center",
            fontSize: 13, color: COLORS.textMuted,
          }}>
            {bp.isMobile && (
              <span style={{ fontWeight: 600, color: COLORS.text, textTransform: "capitalize" }}>
                {tabLabels[activeTab] || activeTab}
              </span>
            )}
            {/* Future: page-context breadcrumb renders here on desktop */}
          </div>
        </div>

        {/* Right side: agent pill + live badge + avatar */}
        <div style={{ display: "flex", alignItems: "center", gap: bp.isMobile ? 8 : 12, flexShrink: 0 }}>
          {/* Live agents count — thinGlass pill matches the rest of the
              modernized chrome (Library toolbar, density toggles, etc.). */}
          {!bp.isMobile && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px",
              ...MATERIALS.thinGlass(),
              borderRadius: 999,
              fontSize: 11, fontWeight: 600,
              color: COLORS.green,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: COLORS.green,
                boxShadow: `0 0 6px ${COLORS.green}88`,
              }} />
              {agents.filter(a => a.status === "live").length} Live
            </div>
          )}
          {/* Help — opens /help/ landing in a new tab. Static HTML served
              from frontend/public/help/ via nginx /help/ alias. See
              docs/runbooks/ for the help-system design rationale. */}
          <a
            href="/help/"
            target="_blank"
            rel="noopener noreferrer"
            title="Help &amp; user guides"
            style={{
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
              ...MATERIALS.thinGlass(),
              borderRadius: 10,
              color: COLORS.textMuted, cursor: "pointer", padding: 0,
              textDecoration: "none",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 4px 14px ${COLORS.accent}26`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <Icon name="help" size={16} ariaLabel="Open help and user guides" color={COLORS.text} />
          </a>
          <button
            onClick={toggleTheme}
            title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
              ...MATERIALS.thinGlass(),
              borderRadius: 10,
              color: COLORS.textMuted, cursor: "pointer", padding: 0,
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 4px 14px ${COLORS.accent}26`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <Icon name={mode === "dark" ? "theme.light" : "theme.dark"} size={16}
              ariaLabel={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              color={COLORS.text} />
          </button>
          <div style={{ position: "relative" }}>
            <div onClick={() => setShowUserMenu(prev => !prev)} style={{
              width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.pink})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer",
            }}>{getInitials(currentUser?.name)}</div>
            {showUserMenu && (
              <div style={{
                position: "absolute", top: 40, right: 0, zIndex: 100,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 10, padding: 8, minWidth: 180,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}>
                <div style={{ padding: "8px 12px", borderBottom: `1px solid ${COLORS.border}`, marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{currentUser?.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>{currentUser?.email}</div>
                  {currentTenant && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>{currentTenant.name}</div>}
                </div>
                <button onClick={() => { setShowUserMenu(false); onLogout?.(); }} style={{
                  width: "100%", padding: "8px 12px", border: "none", borderRadius: 6,
                  background: "transparent", color: COLORS.pink, fontSize: 12,
                  fontWeight: 600, cursor: "pointer", textAlign: "left",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <Icon name="user.signout" size={14} color={COLORS.pink} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BYOK onboarding banner — visible everywhere except Admin (where user is already configuring). */}
      {!llmReadyLoading && llmReady === false && activeTab !== "admin" && (
        <div style={{ padding: "8px 16px 0", position: "relative", zIndex: 1 }}>
          <LLMNotConfigured />
        </div>
      )}

      {/* Main content area — z-index above ambient orb layer */}
      <div style={{
        display: "flex", flex: 1, overflow: "hidden",
        paddingBottom: bp.isMobile ? 60 : 0,
        position: "relative", zIndex: 1,
      }}>
        {!bp.isMobile && <Nav />}
        {activeTab === "builder" && <BuilderView pendingEditAgent={pendingEditAgent} clearPendingEdit={() => setPendingEditAgent(null)} deepLinkAgentId={deepLinkAgentId} />}
        {activeTab === "library" && <AgentLibraryView onEditAgent={(agent) => { setPendingEditAgent(agent); navigate(`/builder/${agent.id}`); }} deepLinkAgentId={deepLinkAgentId} />}
        {activeTab === "marketplace" && <MarketplaceView />}
        {activeTab === "channels" && <ChannelsView currentUser={currentUser} />}
        {activeTab === "campaigns" && <CampaignsView />}
        {activeTab === "schedules" && <SchedulesView />}
        {activeTab === "skills" && <SkillsView />}
        {activeTab === "supervisor" && <SupervisorView currentUser={currentUser} />}
        {/* Phase 1 sub-task E — when URL is /analytics/conversations/:id,
            render the full-page Inspector instead of the analytics dashboard.
            The activeTab is still "analytics" so the sidebar highlights
            correctly. */}
        {/* Phase 1.5 sub-task H frontend (item #2) — Observability hub
            mounts at /analytics/observability/* for Datasets/Prompts/
            Evals/Annotations tabs. Sidebar still highlights Analytics. */}
        {activeTab === "analytics" && location.pathname.match(/^\/analytics\/conversations\/[^/]+/)
          ? <ConversationInspectorPage />
          : activeTab === "analytics" && location.pathname.match(/^\/analytics\/observability/)
          ? <ObservabilityHub />
          : activeTab === "analytics" && <AnalyticsView />}
        {activeTab === "admin" && <AdminView currentUser={currentUser} currentTenant={currentTenant} refreshTenant={refreshTenant} />}
        {activeTab === "chunk-tuning" && <ChunkTuningDocs />}
      </div>

      {/* Mobile bottom nav */}
      {bp.isMobile && <Nav />}

      {/* Floating 412 banner (IntegrationConfigRequired → deep-link). */}
      {configErrorBanner}
    </div>
  );
}
